import { useEffect, useState } from 'react'
import {
  Alert, Box, Button, FormControlLabel, Switch, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material'
import { BellRing, CalendarClock, CircleCheck } from 'lucide-react'
import { Surface, formatDate, tokens } from '@agribot/polygon-checks-ui'
import { useReadiness, useReminders, useSendReminders, useUpdateCampaign, type CampaignRow, type OpenBlocker } from '../api'

export const MISSING: Record<OpenBlocker['missing'][number], string> = {
  materials: 'no materials', level1: 'no buyer (first review)', level2: 'no regional lead (final review)', logins: 'nobody to sign in',
}

/** "14, 7, 1" ⇄ [14, 7, 1]: whole days from 1 to 365, largest first, no repeats. Null when it does not read. */
export function parseDays(text: string): number[] | null {
  const parts = text.split(/[\s,]+/).filter(Boolean)
  const days = parts.map(Number)
  if (days.some((d) => !Number.isInteger(d) || d < 1 || d > 365) || days.length > 10) return null
  return [...new Set(days)].sort((a, b) => b - a)
}

/**
 * How a campaign runs to its dates: whether it opens and closes by itself,
 * when suppliers who have not submitted are reminded, and, while it is a
 * draft, what would stop it opening. All from the API (readiness, the
 * schedule's last problem); admins change it here.
 */
export function CampaignSchedule({ campaign, canEdit }: { campaign: CampaignRow; canEdit: boolean }) {
  const update = useUpdateCampaign(campaign.id)
  const readiness = useReadiness(campaign.id)
  const [scheduled, setScheduled] = useState(campaign.scheduled)
  const [daysText, setDaysText] = useState(campaign.reminderDays.join(', '))
  useEffect(() => { setScheduled(campaign.scheduled); setDaysText(campaign.reminderDays.join(', ')) }, [campaign.scheduled, campaign.reminderDays])
  const days = parseDays(daysText)
  const changed = scheduled !== campaign.scheduled || (days !== null && days.join(',') !== campaign.reminderDays.join(','))
  const closed = campaign.status === 'closed'
  const problem = readiness.data?.scheduleProblem ?? campaign.scheduleProblem

  return (
    <Surface sx={{ p: 5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <CalendarClock size={18} color={tokens.color.brandDeep} />
        <Typography variant="h4" component="h2">Schedule</Typography>
      </Box>
      <FormControlLabel
        control={<Switch checked={scheduled} disabled={!canEdit || closed} onChange={(e) => setScheduled(e.target.checked)} />}
        label={campaign.status === 'draft' ? `Open on ${formatDate(campaign.opensAt)} and close on ${formatDate(campaign.closesAt)} by itself` : `Close on ${formatDate(campaign.closesAt)} by itself`}
      />
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 4, ml: 6 }}>
        Off, an admin opens and closes it here. Turn it on once assignments are done: it will not open while a supplier is not ready.
      </Typography>
      <TextField
        label="Remind suppliers who have not submitted, days before it closes"
        value={daysText}
        onChange={(e) => setDaysText(e.target.value)}
        disabled={!canEdit || closed}
        error={days === null}
        helperText={days === null ? 'Whole days from 1 to 365, separated by commas.' : days.length ? `Reminders go ${days.map((d) => `${d} day${d === 1 ? '' : 's'}`).join(', ')} before ${formatDate(campaign.closesAt)}.` : 'No scheduled reminders.'}
        fullWidth
        InputLabelProps={{ shrink: true }}
      />
      {canEdit && !closed && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
          <Button disabled={!changed || days === null || update.isPending} onClick={() => update.mutate({ scheduled, reminderDays: days ?? [] })}>
            Save schedule
          </Button>
        </Box>
      )}
      {update.isError && <Alert severity="error" sx={{ mt: 3 }}>{(update.error as Error).message}</Alert>}
      {problem && <Alert severity="warning" sx={{ mt: 3 }}>Did not open on its date: {problem}</Alert>}
      {campaign.status === 'draft' && readiness.data && (
        readiness.data.ready
          ? <Alert severity="success" icon={<CircleCheck size={18} />} sx={{ mt: 3 }}>Ready to open: every one of {readiness.data.assigned} suppliers has materials, both reviewers and someone to sign in.</Alert>
          : (
            <Alert severity="info" sx={{ mt: 3, '& .MuiAlert-message': { width: '100%' } }}>
              <Box sx={{ fontWeight: 600, mb: 1 }}>
                {readiness.data.assigned === 0 ? 'Nothing assigned yet.' : `Not ready to open: ${readiness.data.blockers.length} of ${readiness.data.assigned} suppliers.`}
              </Box>
              {readiness.data.blockers.length > 0 && (
                <Box component="ul" sx={{ m: 0, pl: 4 }}>
                  {readiness.data.blockers.slice(0, 8).map((b) => <li key={b.supplierId}>{b.supplier}: {b.missing.map((m) => MISSING[m as keyof typeof MISSING] ?? m).join(', ')}</li>)}
                  {readiness.data.blockers.length > 8 && <li>and {readiness.data.blockers.length - 8} more</li>}
                </Box>
              )}
            </Alert>
          )
      )}
    </Surface>
  )
}

/** Reminders sent so far, scheduled and by hand; an admin can remind everyone who has not submitted now. */
export function CampaignReminders({ campaign, canEdit }: { campaign: CampaignRow; canEdit: boolean }) {
  const { data: reminders, error } = useReminders(campaign.id)
  const send = useSendReminders(campaign.id)
  return (
    <Surface sx={{ p: 5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <BellRing size={18} color={tokens.color.brandDeep} />
        <Typography variant="h4" component="h2" sx={{ flex: 1 }}>Reminders</Typography>
        {canEdit && campaign.status === 'open' && (
          <Button variant="outlined" size="small" disabled={send.isPending} onClick={() => send.mutate(undefined)}>Remind now</Button>
        )}
      </Box>
      {send.data && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => send.reset()}>
          {send.data.emails} email{send.data.emails === 1 ? '' : 's'} to {send.data.suppliers} supplier{send.data.suppliers === 1 ? '' : 's'} that have not submitted.
        </Alert>
      )}
      {send.isError && <Alert severity="error" sx={{ mb: 3 }}>{(send.error as Error).message}</Alert>}
      {error && <Alert severity="error">{(error as Error).message}</Alert>}
      {reminders && reminders.length === 0 && (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {campaign.status === 'open' ? 'None sent yet.' : 'Reminders go once the campaign is open.'}
        </Typography>
      )}
      {reminders && reminders.length > 0 && (
        <Box sx={{ maxHeight: 280, overflow: 'auto' }}>
          <Table size="small" aria-label="Reminders sent">
            <TableHead>
              <TableRow><TableCell>Sent</TableCell><TableCell>Supplier</TableCell><TableCell>Why</TableCell><TableCell align="right">Emails</TableCell></TableRow>
            </TableHead>
            <TableBody>
              {reminders.map((r, i) => (
                <TableRow key={`${r.supplierId}-${r.sentAt}-${i}`}>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(r.sentAt)}</TableCell>
                  <TableCell>{r.supplier}</TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>{r.daysBefore === null ? `By hand${r.sentBy ? `, ${r.sentBy}` : ''}` : `${r.daysBefore} day${r.daysBefore === 1 ? '' : 's'} before close`}</TableCell>
                  <TableCell align="right">{r.emails}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </Surface>
  )
}
